import { useEffect, useRef, useState } from "react";
import { Building2, Check, ExternalLink, Globe2, LoaderCircle, MailSearch, MapPin, Search, Save, Star, UserSearch, X } from "lucide-react";
import { emailScannerStatus, scanWebsiteForEmails } from "../emailScanner";
import { EMAIL_SCANNER_EXTENSION } from "../projectSettings";
import { fetchGooglePlace, placesApiConfigured, searchGooglePlaces } from "../googlePlaces";
import { industryForContact, industryLabelForPlaceSelection, industryLabel, normalizeIndustry, suggestedIndustries } from "../industries";
import { extractGooglePlaceId, googleRatingLabel, googleRatingTone, isGoogleMapsUrl, normalizePhone } from "../utils";
import ExtensionSetupDialog from "./ExtensionSetupDialog";

const emptyForm = {
  companyName: "",
  contactName: "",
  contactRole: "",
  contactSource: "",
  email: "",
  emailSource: "",
  phone: "",
  countryCode: "US",
  line1: "",
  line2: "",
  city: "",
  state: "",
  postalCode: "",
  website: "",
  websiteStatus: "none",
  websiteQuality: "none",
  websiteNotes: "",
  leadSource: "manual",
  industry: "",
  campaign: "",
  country: "US",
  enrichmentStatus: "pending",
  googleMapsUrl: "",
  googlePlaceId: "",
  googleRating: null,
  googleRatingCount: 0,
  status: "new",
  previewEnabled: true
};

function fromContact(contact) {
  if (!contact) return emptyForm;
  return {
    companyName: contact.companyName || "",
    contactName: contact.contactName || "",
    contactRole: contact.contactRole || "",
    contactSource: contact.contactSource || "",
    email: contact.email || "",
    emailSource: contact.emailSource || "",
    phone: contact.phone?.e164 || contact.phone?.raw || "",
    countryCode: contact.address?.countryCode || "US",
    line1: contact.address?.line1 || "",
    line2: contact.address?.line2 || "",
    city: contact.address?.city || "",
    state: contact.address?.state || "",
    postalCode: contact.address?.postalCode || "",
    website: contact.website || "",
    websiteStatus: contact.websiteStatus || (contact.website ? "available" : "none"),
    websiteQuality: contact.websiteQuality || (contact.website ? (/^https:/i.test(contact.website) ? "average" : "poor") : "none"),
    websiteNotes: contact.websiteNotes || "",
    leadSource: contact.leadSource || "manual",
    industry: industryLabel(industryForContact(contact)),
    campaign: contact.campaign || "",
    country: contact.country || contact.address?.countryCode || "US",
    enrichmentStatus: contact.enrichmentStatus || "pending",
    googleMapsUrl: contact.googleMapsUrl || "",
    googlePlaceId: contact.googlePlaceId || "",
    googleRating: Number.isFinite(contact.googleRating) ? contact.googleRating : null,
    googleRatingCount: Number.isFinite(contact.googleRatingCount) ? contact.googleRatingCount : 0,
    status: contact.status || "new",
    previewEnabled: contact.previewEnabled !== false
  };
}

export default function ContactForm({ contact, onClose, onSave }) {
  const formRef = useRef(null);
  const preserveIndustryRef = useRef(Boolean(contact));
  const [form, setForm] = useState(() => fromContact(contact));
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [placeQuery, setPlaceQuery] = useState("");
  const [placeResults, setPlaceResults] = useState([]);
  const [placeStatus, setPlaceStatus] = useState("idle");
  const [placeMessage, setPlaceMessage] = useState("");
  const [emailScan, setEmailScan] = useState({ status: "idle", message: "", emails: [], people: [] });
  const [extensionSetupOpen, setExtensionSetupOpen] = useState(false);
  const scannerStatus = emailScannerStatus(EMAIL_SCANNER_EXTENSION.currentVersion);

  useEffect(() => {
    preserveIndustryRef.current = Boolean(contact);
    setForm(fromContact(contact));
  }, [contact]);

  useEffect(() => {
    function saveWithKeyboard(event) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "s") {
        event.preventDefault();
        if (!busy) formRef.current?.requestSubmit();
      }
    }
    window.addEventListener("keydown", saveWithKeyboard);
    return () => window.removeEventListener("keydown", saveWithKeyboard);
  }, [busy]);

  function update(event) {
    const { name, value, checked, type } = event.target;
    if (name === "industry") preserveIndustryRef.current = Boolean(normalizeIndustry(value));
    setForm((current) => ({ ...current, [name]: type === "checkbox" ? checked : value }));
  }

  function applyPlace(place) {
    const phone = normalizePhone(place.internationalPhoneNumber, place.address.countryCode || "US");
    const hasWebsite = Boolean(place.websiteURI);
    const hasSsl = /^https:/i.test(place.websiteURI || "");
    const preserveExisting = preserveIndustryRef.current;
    setForm((current) => ({
      ...current,
      companyName: place.displayName || current.companyName,
      phone: phone.valid ? phone.e164 || phone.raw : current.phone,
      countryCode: place.address.countryCode || current.countryCode,
      line1: place.address.line1 || current.line1,
      line2: place.address.line2 || "",
      city: place.address.city || current.city,
      state: place.address.state || current.state,
      postalCode: place.address.postalCode || current.postalCode,
      website: place.websiteURI || current.website,
      websiteStatus: hasWebsite ? "available" : "none",
      websiteQuality: hasWebsite ? (hasSsl ? "average" : "poor") : "none",
      websiteNotes: hasWebsite ? (hasSsl ? "HTTPS vorhanden; Design und Inhalt noch nicht manuell bewertet." : "Die veröffentlichte Website verwendet kein HTTPS (kein SSL).") : "Google Places lieferte keine Unternehmenswebsite.",
      leadSource: "google_maps",
      industry: industryLabelForPlaceSelection(place, current.industry, { preserveExisting }),
      country: place.address.countryCode || current.country,
      googleMapsUrl: place.googleMapsURI || current.googleMapsUrl,
      googlePlaceId: place.id || current.googlePlaceId,
      googleRating: Number.isFinite(place.rating) ? place.rating : null,
      googleRatingCount: Number.isFinite(place.userRatingCount) ? place.userRatingCount : 0
    }));
    setPlaceResults([]);
    setPlaceStatus("selected");
    setPlaceMessage(`${place.displayName || "Unternehmen"} wurde von Google Places übernommen.`);
  }

  async function choosePlace(placeId) {
    setPlaceStatus("loading");
    setPlaceMessage("Google-Unternehmensdaten werden geladen …");
    try {
      applyPlace(await fetchGooglePlace(placeId));
    } catch (placeError) {
      setPlaceStatus("error");
      setPlaceMessage(placeError.message || "Google-Unternehmensdaten konnten nicht geladen werden.");
    }
  }

  async function lookupPlace(event) {
    event.preventDefault();
    if (placeStatus === "loading") return;
    const query = placeQuery.trim();
    if (!query) return;
    setPlaceStatus("loading");
    setPlaceMessage("");
    setPlaceResults([]);
    try {
      const isUrl = /^https?:\/\//i.test(query);
      const placeId = isUrl ? extractGooglePlaceId(query) : "";
      if (placeId) {
        await choosePlace(placeId);
        return;
      }
      if (isUrl) {
        throw new Error("Dieser Maps-Link enthält keine direkt lesbare Place ID. Bitte den Firmennamen suchen oder einen vollständigen Google-Maps-Link verwenden.");
      }
      const results = await searchGooglePlaces(query);
      setPlaceResults(results);
      setPlaceStatus(results.length ? "results" : "error");
      setPlaceMessage(results.length ? `${results.length} Google-Treffer gefunden.` : "Kein passendes Unternehmen gefunden.");
    } catch (placeError) {
      setPlaceStatus("error");
      setPlaceMessage(placeError.message || "Google Places konnte nicht durchsucht werden.");
    }
  }

  async function lookupEmail() {
    if (!form.website.trim()) return;
    if (!scannerStatus.isChrome || !scannerStatus.isCurrent) {
      if (scannerStatus.isChrome) setExtensionSetupOpen(true);
      return;
    }
    setEmailScan({ status: "loading", message: "Auffindbare Seiten werden in Prioritätsreihenfolge analysiert …", emails: [], people: [] });
    try {
      const result = await scanWebsiteForEmails(form.website);
      const emails = result.emails || [];
      const people = result.people || [];
      const scannedCount = result.scannedUrls?.length || 0;
      const limitNote = result.truncated ? ` Das Sicherheitslimit von ${result.limits?.maxPages || 100} Seiten beziehungsweise ${result.limits?.maxSeconds || 75} Sekunden wurde erreicht.` : "";
      if (!emails.length && !people.length) {
        setEmailScan({
          status: "warning",
          message: `Keine öffentliche E-Mail-Adresse oder Geschäftsleitung auf ${scannedCount} geprüften Seiten gefunden.${limitNote}`,
          emails: [],
          people: []
        });
        return;
      }
      setForm((current) => ({
        ...current,
        email: current.email || emails[0]?.email || "",
        emailSource: current.emailSource || emails[0]?.sourceUrl || "",
        contactName: current.contactName || people[0]?.name || "",
        contactRole: current.contactRole || people[0]?.role || "",
        contactSource: current.contactSource || people[0]?.sourceUrl || ""
      }));
      setEmailScan({
        status: "selected",
        message: `${emails.length} ${emails.length === 1 ? "E-Mail-Adresse" : "E-Mail-Adressen"} und ${people.length} ${people.length === 1 ? "möglicher Ansprechpartner" : "mögliche Ansprechpartner"} auf ${scannedCount} Seiten gefunden.${limitNote}`,
        emails,
        people
      });
    } catch (scanError) {
      setEmailScan({ status: "error", message: scanError.message || "Die Website konnte nicht geprüft werden.", emails: [], people: [] });
    }
  }

  function chooseEmail(candidate) {
    setForm((current) => ({ ...current, email: candidate.email, emailSource: candidate.sourceUrl || "" }));
    setEmailScan((current) => ({ ...current, status: "selected", message: `${candidate.email} wurde übernommen.` }));
  }

  function choosePerson(candidate) {
    setForm((current) => ({ ...current, contactName: candidate.name, contactRole: candidate.role || "", contactSource: candidate.sourceUrl || "" }));
    setEmailScan((current) => ({ ...current, status: "selected", message: `${candidate.name} wurde als Ansprechpartner übernommen.` }));
  }

  async function submit(event) {
    event.preventDefault();
    setError("");
    const phone = normalizePhone(form.phone, form.countryCode);
    if (!form.companyName.trim()) return setError("Der Firmenname ist erforderlich.");
    if (!form.city.trim()) return setError("Bitte eine Stadt angeben.");
    if (!normalizeIndustry(form.industry)) return setError("Bitte eine Branche auswählen oder eintragen.");
    if (!phone.valid) return setError("Die Telefonnummer ist ungültig. Bitte Landesvorwahl oder Land prüfen.");
    if (form.previewEnabled && !phone.e164) return setError("Für eine aktive Website-Vorschau ist eine Telefonnummer erforderlich.");
    if (form.googleMapsUrl && !isGoogleMapsUrl(form.googleMapsUrl)) {
      return setError("Bitte einen gültigen Google-Maps-Link einfügen.");
    }
    setBusy(true);
    try {
      await onSave({
        ...form,
        companyName: form.companyName.trim(),
        contactName: form.contactName.trim(),
        contactRole: form.contactRole.trim(),
        contactSource: form.contactSource.trim(),
        email: form.email.trim().toLowerCase(),
        emailSource: form.emailSource.trim(),
        phone,
        website: form.website.trim(),
        googlePlaceId: form.googlePlaceId || extractGooglePlaceId(form.googleMapsUrl),
        industry: normalizeIndustry(form.industry)
      });
    } catch (saveError) {
      setError(saveError.message || "Kontakt konnte nicht gespeichert werden.");
      setBusy(false);
    }
  }

  const placeId = form.googlePlaceId || extractGooglePlaceId(form.googleMapsUrl);

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="drawer" role="dialog" aria-modal="true" aria-labelledby="contact-form-title">
        <header className="drawer-header">
          <div><p className="eyebrow">{contact ? "Kontakt bearbeiten" : "Neuer Prospect"}</p><h2 id="contact-form-title">Kontaktdaten</h2></div>
          <button className="icon-button" onClick={onClose} aria-label="Schließen"><X /></button>
        </header>
        <form ref={formRef} className="drawer-form" onSubmit={submit}>
          <div className="form-section">
            <h3>Unternehmen automatisch übernehmen</h3>
            <div className="places-lookup">
              <label className="field field-full">
                <span>Firmenname oder vollständiger Google-Maps-Link</span>
                <div className="places-search-row">
                  <div className="input-action"><Search /><input value={placeQuery} onChange={(event) => setPlaceQuery(event.target.value)} onKeyDown={(event) => event.key === "Enter" && !event.nativeEvent.isComposing && lookupPlace(event)} placeholder="z. B. Summit Roofing Fort Worth" disabled={!placesApiConfigured || placeStatus === "loading"} /></div>
                  <button type="button" className="button primary" onClick={lookupPlace} disabled={!placesApiConfigured || !placeQuery.trim() || placeStatus === "loading"}>{placeStatus === "loading" ? <LoaderCircle className="spin" /> : <Search />} Suchen</button>
                </div>
              </label>
              {!placesApiConfigured && <div className="places-message warning"><Globe2 /><span>Google Places ist vorbereitet, aber noch nicht aktiviert. Es fehlt der eingeschränkte Browser-API-Key.</span></div>}
              {placeMessage && placesApiConfigured && <div className={`places-message ${placeStatus}`}>
                {placeStatus === "loading" ? <LoaderCircle className="spin" /> : placeStatus === "selected" ? <Check /> : <MapPin />}
                <span>{placeMessage}</span>
              </div>}
              {placeResults.length > 0 && <div className="place-results" role="listbox" aria-label="Google-Unternehmen">
                {placeResults.map((result) => <button type="button" key={result.id} onClick={() => choosePlace(result.id)}>
                  <Building2 /><span><strong>{result.displayName}</strong><small>{[result.primaryTypeDisplayName, result.formattedAddress || "Adresse nicht angegeben"].filter(Boolean).join(" · ")}</small>{Number.isFinite(result.rating) && <span className={`google-rating rating-${googleRatingTone(result.rating)}`}><Star /><b>{result.rating.toFixed(1)}</b><span>{googleRatingLabel(result.rating)} · {result.userRatingCount.toLocaleString("de-DE")} Bewertungen</span></span>}</span><Check />
                </button>)}
              </div>}
            </div>
            <label className="field field-full">
              <span>Link zum Google Maps Profil</span>
              <div className="input-action"><MapPin /><input name="googleMapsUrl" type="url" value={form.googleMapsUrl} onChange={update} placeholder="https://www.google.com/maps/place/…" />{form.googleMapsUrl && <a href={form.googleMapsUrl} target="_blank" rel="noreferrer" aria-label="Maps öffnen"><ExternalLink /></a>}</div>
              <small>Bei unterstützten Links wird die Place ID erkannt; verkürzte maps.app.goo.gl-Links benötigen einen kleinen Resolver.</small>
            </label>
            {placeId && <div className="place-id">Place ID erkannt: <code>{placeId}</code></div>}
            {Number.isFinite(form.googleRating) && <div className={`selected-google-rating rating-${googleRatingTone(form.googleRating)}`}><Star /><strong>{form.googleRating.toFixed(1)}</strong><span>{googleRatingLabel(form.googleRating)} · {form.googleRatingCount.toLocaleString("de-DE")} Google-Bewertungen</span></div>}
          </div>

          <div className="form-section form-grid">
            <h3 className="field-full">Kontakt</h3>
            <label className="field"><span>Firmenname *</span><input name="companyName" value={form.companyName} onChange={update} required /></label>
            <label className="field"><span>Ansprechpartner</span><input name="contactName" value={form.contactName} onChange={update} /></label>
            <label className="field"><span>Rolle des Ansprechpartners</span><input name="contactRole" value={form.contactRole} onChange={update} placeholder="Owner, President, …" /></label>
            <label className="field"><span>Quelle des Ansprechpartners</span><input name="contactSource" type="url" value={form.contactSource} onChange={update} placeholder="https://…" /></label>
            <label className="field"><span>E-Mail</span><input name="email" type="email" value={form.email} onChange={update} /></label>
            <label className="field"><span>Quelle der E-Mail</span><input name="emailSource" type="url" value={form.emailSource} onChange={update} placeholder="https://…" /></label>
            <label className="field"><span>Telefonnummer</span><input name="phone" type="tel" value={form.phone} onChange={update} placeholder="(214) 555-0196" /></label>
            <label className="field field-full">
              <span>Website</span>
              <div className={`website-scan-row ${!scannerStatus.isChrome ? "without-action" : ""}`}>
                <input name="website" type="url" value={form.website} onChange={update} placeholder="https://…" />
                {scannerStatus.isChrome && scannerStatus.isCurrent && <button type="button" className="button secondary" onClick={lookupEmail} disabled={!form.website.trim() || emailScan.status === "loading"}>{emailScan.status === "loading" ? <LoaderCircle className="spin" /> : <MailSearch />} Website analysieren</button>}
                {scannerStatus.isChrome && !scannerStatus.isCurrent && <button type="button" className="button secondary" onClick={() => setExtensionSetupOpen(true)}><MailSearch /> {scannerStatus.isInstalled ? "Scanner aktualisieren" : "Scanner einrichten"}</button>}
              </div>
              {scannerStatus.isChrome ? <small>{scannerStatus.isCurrent ? `Scanner ${scannerStatus.installedVersion} aktiv. Prüft bis zu 100 interne Seiten nach E-Mails und Geschäftsleitung.` : scannerStatus.isInstalled ? `Installierte Version ${scannerStatus.installedVersion}; benötigt wird ${EMAIL_SCANNER_EXTENSION.currentVersion}.` : "Chrome erkannt. Für die automatische Suche fehlt noch die LeadMagnet-Erweiterung."}</small> : <small className="chrome-required">Die automatische Website-Analyse ist nur in Google Chrome verfügbar. Bitte diesen Admin-Bereich dafür in Chrome öffnen.</small>}
            </label>
            {emailScan.message && <div className={`places-message ${emailScan.status} field-full`}>
              {emailScan.status === "loading" ? <LoaderCircle className="spin" /> : emailScan.status === "selected" ? <Check /> : <MailSearch />}
              <span>{emailScan.message}</span>
            </div>}
            {emailScan.emails.length > 0 && <div className="place-results email-results field-full" aria-label="Gefundene E-Mail-Adressen">
              <p className="result-group-label">Gefundene E-Mail-Adressen</p>
              {emailScan.emails.map((candidate) => <button type="button" key={candidate.email} onClick={() => chooseEmail(candidate)}>
                <MailSearch /><span><strong>{candidate.email}</strong><small>{candidate.sourceUrl}</small></span><Check />
              </button>)}
            </div>}
            {emailScan.people.length > 0 && <div className="place-results people-results field-full" aria-label="Gefundene Ansprechpartner">
              <p className="result-group-label">Mögliche Geschäftsleitung / Inhaber</p>
              {emailScan.people.map((candidate) => <button type="button" key={`${candidate.name}-${candidate.role}`} onClick={() => choosePerson(candidate)}>
                <UserSearch /><span><strong>{candidate.name}</strong><small>{candidate.role} · {candidate.sourceUrl}</small></span><Check />
              </button>)}
            </div>}
            <label className="field compact"><span>Land für Telefonformat</span><select name="countryCode" value={form.countryCode} onChange={update}><option value="US">USA (+1)</option><option value="CA">Kanada (+1)</option><option value="GB">UK (+44)</option><option value="DE">Deutschland (+49)</option><option value="AT">Österreich (+43)</option><option value="CH">Schweiz (+41)</option></select></label>
            <label className="field compact"><span>Status</span><select name="status" value={form.status} onChange={update}><option value="new">Neu</option><option value="contacted">Kontaktiert</option><option value="not_reached">Nicht erreicht</option><option value="follow_up">Follow-up</option><option value="review">Nachprüfen</option><option value="postponed">Zurückgestellt</option><option value="interested">Interessiert</option><option value="closed">Abgeschlossen</option><option value="not_interested">Kein Interesse</option></select></label>
          </div>

          <div className="form-section form-grid">
            <h3 className="field-full">Anschrift</h3>
            <label className="field field-full"><span>Straße und Hausnummer</span><input name="line1" value={form.line1} onChange={update} /></label>
            <label className="field field-full"><span>Adresszusatz</span><input name="line2" value={form.line2} onChange={update} /></label>
            <label className="field"><span>Stadt *</span><input name="city" value={form.city} onChange={update} required /></label>
            <label className="field"><span>Bundesstaat / Region</span><input name="state" value={form.state} onChange={update} /></label>
            <label className="field"><span>Postleitzahl</span><input name="postalCode" value={form.postalCode} onChange={update} /></label>
          </div>

          <div className="form-section form-grid">
            <h3 className="field-full">Akquise &amp; Anreicherung</h3>
            <label className="field"><span>Lead-Quelle</span><input name="leadSource" value={form.leadSource} onChange={update} placeholder="google_maps" /></label>
            <label className="field"><span>Kampagne</span><input name="campaign" value={form.campaign} onChange={update} placeholder="hurricane_roofing_usa_2026" /></label>
            <label className="field"><span>Branche / Label *</span><input name="industry" list="industry-options" value={form.industry} onChange={update} placeholder="Roofer" required /><datalist id="industry-options">{suggestedIndustries.map(({ value, label }) => <option key={value} value={label} />)}</datalist><small>Bei neuen Kontakten schlägt Google Maps die Branche vor. Eine gespeicherte oder manuell gewählte Branche bleibt beim Abgleich erhalten.</small></label>
            <label className="field"><span>Land</span><input name="country" value={form.country} onChange={update} maxLength="2" placeholder="US" /></label>
            <label className="field"><span>Website-Status</span><select name="websiteStatus" value={form.websiteStatus} onChange={update}><option value="none">Keine Website</option><option value="available">Vorhanden</option></select></label>
            <label className="field"><span>Website-Qualität</span><select name="websiteQuality" value={form.websiteQuality} onChange={update}><option value="none">Keine</option><option value="poor">Schlecht</option><option value="average">Durchschnittlich</option><option value="good">Gut</option></select></label>
            <label className="field"><span>Anreicherungsstatus</span><select name="enrichmentStatus" value={form.enrichmentStatus} onChange={update}><option value="pending">Ausstehend</option><option value="partial">Teilweise</option><option value="complete">Vollständig</option></select></label>
            <label className="field field-full"><span>Website-Hinweis</span><textarea name="websiteNotes" value={form.websiteNotes} onChange={update} maxLength="1000" placeholder="Technische oder qualitative Auffälligkeiten …" /></label>
          </div>

          <label className="check-row"><input type="checkbox" name="previewEnabled" checked={form.previewEnabled} onChange={update} /><span><strong>Persönliche Website-Vorschau aktivieren</strong><small>Veröffentlicht nur die für die Demo benötigten Geschäftsdaten unter einer nicht erratbaren ID.</small></span></label>
          {error && <div className="error-box" role="alert">{error}</div>}
          <footer className="drawer-actions"><button type="button" className="button ghost" onClick={onClose}>Abbrechen</button><button className="button primary" disabled={busy} title="Kontakt speichern (⌘S)"><Save />{busy ? "Speichert …" : "Kontakt speichern"}</button></footer>
        </form>
      </section>
      {extensionSetupOpen && <ExtensionSetupDialog scannerStatus={scannerStatus} onClose={() => setExtensionSetupOpen(false)} />}
    </div>
  );
}
